/**
 * @file        src/pages/testimonial/TestimonialList.jsx
 * @description Testimonial List. Columns: # · Name · Image · Detail
 *              (email/phone/designation/company/star) · Description · Status ·
 *              Action. Backed by /api/testimonials.
 * @author      Kinetika Labs
 * @created     2026-07-03
 * @changelog
 *   - 2026-07-03 Claude — Initial (Testimonial Management).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

const stars = (n) => '★'.repeat(Math.min(5, Math.max(0, Number(n) || 0)));

// Characters of the description shown before "…more" — about two lines in the
// 420px column, which is what the row was sized for.
const DESC_LIMIT = 110;

export default function TestimonialList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  // Rows whose description is expanded in place. This replaces the native `title`
  // tooltip, which the browser renders as an unstyled OS bubble over the table —
  // it is not part of the console's UI and cannot be made to match it.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleRow = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const qc = useQueryClient();
  const params = { page, pageSize };
  if (applied) params.q = applied;

  const { data, isLoading } = useQuery({
    queryKey: qk.testimonials.list(params),
    queryFn: () => get('/testimonials', { params }),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/testimonials/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Testimonial removed.');
      qc.invalidateQueries({ queryKey: ['testimonials'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  async function onDelete(row) {
    if (!(await confirm(`Delete testimonial from “${row.name}”?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="Testimonial List"
        subtitle="Testimonial Management"
        actions={<Link to="/testimonials/new" className="btn primary"><Icon name="plus" /> Add Testimonial</Link>}
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
          <div className="field">
            <label>Name / Company</label>
            <input placeholder="Search testimonial…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Name</th><th>Image</th><th>Detail</th><th>Description</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No testimonials yet. Add your first one.</td></tr>
                ) : rows.map((t, idx) => (
                  // The quote makes this row 3 lines tall, so every other cell must
                  // sit in the MIDDLE of it — otherwise Status and the icons float
                  // against the top edge while the text runs past them.
                  <tr key={t.id} style={{ verticalAlign: 'middle' }}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><b>{t.name}</b></td>
                    <td>{t.image_url
                      ? <img src={t.image_url} alt={t.name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} />
                      : <span className="muted">—</span>}</td>
                    <td style={{ maxWidth: 260 }}>
                      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                        {t.email ? <div><b>Email:</b> {t.email}</div> : null}
                        {t.phone ? <div><b>Phone:</b> {t.phone}</div> : null}
                        {t.designation ? <div><b>Designation:</b> {t.designation}</div> : null}
                        {t.company ? <div><b>Company:</b> {t.company}</div> : null}
                        <div><b>Star:</b> <span style={{ color: '#f5a623' }}>{stars(t.star)}</span> {t.star}</div>
                      </div>
                    </td>
                    {/* `maxWidth` on a <td> alone does nothing under auto table
                        layout — the cell just grows and the text runs under Status
                        and Action. The fixed width goes on a block INSIDE the cell.
                        No `title`: that is the grey OS tooltip, which ignores the
                        console's styling entirely. */}
                    <td>
                      {(() => {
                        // Strip quotes the author typed around their own quote —
                        // otherwise they double up with the quote mark we render.
                        const full = (t.description || '').replace(/^["“”\s]+|["“”\s]+$/g, '');
                        const isOpen = expanded.has(t.id);
                        const long = full.length > DESC_LIMIT;
                        // Truncated by CHARACTER count, not a CSS line-clamp: a
                        // clamped block hides its overflow, so a link placed after it
                        // would drop onto its own line. Slicing lets "…more" finish
                        // the sentence inline, exactly where the text stops.
                        const shown = !long || isOpen ? full : `${full.slice(0, DESC_LIMIT).trimEnd()}…`;
                        if (!full) return <span className="muted">—</span>;
                        return (
                          // A testimonial is a QUOTE, so it should read as one: a
                          // hanging quote mark, italic, an accent rule down the left.
                          // The same words in plain grey body text read as a paragraph
                          // dumped in a cell — which is what this looked like.
                          <blockquote
                            style={{
                              margin: 0,
                              width: 420,
                              maxWidth: 420,
                              // `.table tbody td` sets white-space:nowrap, so the text
                              // could not wrap at ANY width — it ran as one long line
                              // straight under Status and Action. The width only means
                              // something once wrapping is allowed.
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                              position: 'relative',
                              paddingLeft: 14,
                              borderLeft: '2px solid var(--stroke)',
                              fontSize: 13,
                              lineHeight: 1.6,
                              fontStyle: 'italic',
                              color: 'var(--text)',
                            }}
                          >
                            <span
                              aria-hidden="true"
                              style={{
                                position: 'absolute',
                                left: 4,
                                top: -2,
                                fontSize: 18,
                                lineHeight: 1,
                                fontStyle: 'normal',
                                color: 'var(--muted)',
                                opacity: 0.5,
                              }}
                            >
                              ❝
                            </span>
                            {shown}
                            {long && (
                              <button
                                type="button"
                                className="link-btn"
                                onClick={() => toggleRow(t.id)}
                                aria-expanded={isOpen}
                                style={{ marginLeft: 4, fontStyle: 'normal' }}
                              >
                                {isOpen ? 'less' : 'more'}
                              </button>
                            )}
                          </blockquote>
                        );
                      })()}
                    </td>
                    <td style={{ verticalAlign: 'middle' }}>
                      <Chip tone={t.is_active ? 'ok' : 'rej'}>{t.is_active ? 'Active' : 'Inactive'}</Chip>
                    </td>
                    {/* NOT `.row-act` on the <td>: that class is display:flex, which
                        makes the CELL a flex container and discards the table's
                        vertical-align — so the icons sat higher than the Active chip
                        next to them. Keep the cell a normal table-cell (centred) and
                        put the flex row on a div INSIDE it. */}
                    <td style={{ verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Link to={`/testimonials/${t.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                        <button className="ib del" aria-label="Delete" onClick={() => onDelete(t)}><Icon name="trash" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
